/**
 * Various effect renderers for the betting zone
 */

import { HeatPoint, FloatingText } from "../types";
import { ANIMATION_CONFIG, COLORS, ZONE_CONFIG } from "../constants";
import { smoothPoints, drawGlowPath, opacityToHex } from "../utils/canvasUtils";

/**
 * Renders zone gradients (left and right sides)
 */
export function drawZoneGradients(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  zoneAlpha: number = ZONE_CONFIG.ZONE_ALPHA,
  victoryPhase?: string,
  winner?: "A" | "B",
  transferProgress: number = 0
): void {
  const centerX = width / 2;

  if (victoryPhase !== "idle" && winner) {
    const loserIsLeft = winner === "B";

    // Winner's side - vertical gradient from bottom
    const winnerGradient = ctx.createLinearGradient(0, height, 0, 0);
    const winnerColor = loserIsLeft ? COLORS.right : COLORS.left;
    winnerGradient.addColorStop(0, winnerColor.glow.replace("0.3", String(zoneAlpha * 1.5)));
    winnerGradient.addColorStop(0.5, winnerColor.glow.replace("0.3", String(zoneAlpha * 0.8)));
    winnerGradient.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = winnerGradient;
    ctx.fillRect(loserIsLeft ? centerX : 0, 0, centerX, height);

    // Loser's side - draining with vertical fade
    const loserColor = loserIsLeft ? COLORS.left : COLORS.right;
    const loserX = loserIsLeft ? 0 : centerX;

    // Overall fade based on victory phase and transfer progress
    const overallOpacity =
      victoryPhase === "complete"
        ? 0.1 // Keep it mostly faded after completion
        : victoryPhase === "transfer"
        ? Math.max(0.1, 1 - transferProgress * 0.9) // Fade during transfer
        : 1; // Full opacity during meter-overdrive and elimination

    // Draw vertical gradient from bottom for loser side
    const loserGradient = ctx.createLinearGradient(0, height, 0, 0);
    loserGradient.addColorStop(0, loserColor.glow.replace("0.3", String(zoneAlpha * 1.5 * overallOpacity)));
    loserGradient.addColorStop(0.5, loserColor.glow.replace("0.3", String(zoneAlpha * 0.8 * overallOpacity)));
    loserGradient.addColorStop(1, "rgba(0, 0, 0, 0)");

    ctx.fillStyle = loserGradient;
    ctx.fillRect(loserX, 0, centerX, height);

    // Add a vertical fade overlay to enhance the draining effect
    if (transferProgress > 0) {
      const fadeGradient = ctx.createLinearGradient(0, 0, 0, height);

      if (transferProgress >= 1 || victoryPhase === "complete") {
        // Fully drained - keep it dark
        fadeGradient.addColorStop(0, "rgba(0, 0, 0, 0.8)");
        fadeGradient.addColorStop(1, "rgba(0, 0, 0, 0.8)");
      } else {
        // Draining in progress
        const fadePoint = transferProgress;
        fadeGradient.addColorStop(0, "rgba(0, 0, 0, 0.9)");
        fadeGradient.addColorStop(
          Math.max(0, fadePoint - 0.2),
          "rgba(0, 0, 0, 0.7)"
        );
        fadeGradient.addColorStop(fadePoint, "rgba(0, 0, 0, 0.3)");
        fadeGradient.addColorStop(
          Math.min(1, fadePoint + 0.1),
          "rgba(0, 0, 0, 0)"
        );
        fadeGradient.addColorStop(1, "rgba(0, 0, 0, 0)");
      }

      ctx.fillStyle = fadeGradient;
      ctx.fillRect(loserX, 0, centerX, height);
    }
  } else {
    // Normal rendering - vertical gradients from bottom for both sides
    // Left side gradient
    const leftGradient = ctx.createLinearGradient(0, height, 0, 0);
    leftGradient.addColorStop(0, COLORS.left.glow.replace("0.3", String(zoneAlpha * 1.5)));
    leftGradient.addColorStop(0.5, COLORS.left.glow.replace("0.3", String(zoneAlpha * 0.8)));
    leftGradient.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = leftGradient;
    ctx.fillRect(0, 0, centerX, height);

    // Right side gradient
    const rightGradient = ctx.createLinearGradient(0, height, 0, 0);
    rightGradient.addColorStop(0, COLORS.right.glow.replace("0.3", String(zoneAlpha * 1.5)));
    rightGradient.addColorStop(0.5, COLORS.right.glow.replace("0.3", String(zoneAlpha * 0.8)));
    rightGradient.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = rightGradient;
    ctx.fillRect(centerX, 0, centerX, height);
  }
}

/**
 * Renders power gradient effect
 */
export function drawPowerGradient(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number
): void {
  const powerGradient = ctx.createLinearGradient(0, height, 0, 0);
  powerGradient.addColorStop(0, "rgba(0, 0, 0, 0)");
  powerGradient.addColorStop(
    0.5,
    `rgba(0, 255, 255, ${ZONE_CONFIG.POWER_GRADIENT_ALPHA * 0.5})`
  );
  powerGradient.addColorStop(
    1,
    `rgba(0, 255, 255, ${ZONE_CONFIG.POWER_GRADIENT_ALPHA})`
  );

  ctx.fillStyle = powerGradient;
  ctx.fillRect(0, 0, width, height);
}

/**
 * Renders the ghost center line
 */
export function drawGhostCenterLine(
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  ghostLinePoints: number[],
  themeColors?: any
): void {
  const smoothedPoints = smoothPoints(ghostLinePoints);
  const centerX = width / 2;

  if (!smoothedPoints.some((p) => Math.abs(p) > 0.1)) return;

  ctx.save();

  const maxDistortion = Math.max(...smoothedPoints.map(Math.abs));
  const intensity = Math.min(maxDistortion / 100, 1);

  ctx.globalCompositeOperation = "screen";

  // Draw the ghost line with glow
  const drawPath = () => {
    ctx.beginPath();
    ctx.moveTo(centerX + smoothedPoints[0], 0);
    for (let i = 1; i < smoothedPoints.length; i++) {
      const y = (i / (smoothedPoints.length - 1)) * height;
      const x = centerX + smoothedPoints[i];
      ctx.lineTo(x, y);
    }
  };

  // Use theme colors if provided, otherwise default to silver/white
  const lineColorHex = themeColors?.grid?.lineColorHex || '#e5e7eb';
  
  // Convert hex to RGB for rgba usage
  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : { r: 229, g: 231, b: 235 }; // Default to gray-200
  };
  
  const rgb = hexToRgb(lineColorHex);
  
  // Outer glow
  ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${0.6 * intensity})`;
  ctx.lineWidth = 3;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  drawPath();
  ctx.stroke();

  // Inner bright core
  ctx.strokeStyle = `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${0.9 * intensity})`;
  ctx.lineWidth = 1;
  drawPath();
  ctx.stroke();

  ctx.restore();
}

/**
 * Heat map renderer class
 */
export class HeatMapRenderer {
  private heatPoints: HeatPoint[] = [];
  private isMobile: boolean = false;
  private maxHeatPoints: number = 20;
  
  constructor(isMobile: boolean = false) {
    this.isMobile = isMobile;
    this.maxHeatPoints = isMobile ? 10 : 20;
  }

  addHeatPoint(x: number, y: number, intensity: number, radius: number): void {
    // Limit heat points on mobile
    if (this.heatPoints.length >= this.maxHeatPoints) {
      // Remove oldest heat point
      this.heatPoints.shift();
    }
    this.heatPoints.push({ x, y, intensity, radius });
  }

  updateAndDraw(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number
  ): void {
    if (this.isMobile) {
      // Skip heat map entirely on mobile for best performance
      this.heatPoints = [];
      return;
    }
    
    ctx.save();
    ctx.globalCompositeOperation = "screen";

    this.heatPoints = this.heatPoints.filter((heat) => {
      heat.intensity *= ANIMATION_CONFIG.HEAT_DECAY_RATE;

      if (heat.intensity < ANIMATION_CONFIG.HEAT_MIN_INTENSITY) return false;

      const x = heat.x * width;
      const y = heat.y * height;

      const heatGradient = ctx.createRadialGradient(x, y, 0, x, y, heat.radius);
      const heatColor = x < width / 2 ? COLORS.left.glow : COLORS.right.glow;

      heatGradient.addColorStop(
        0,
        heatColor.replace("0.3", String(heat.intensity * 0.5))
      );
      heatGradient.addColorStop(1, "rgba(0, 0, 0, 0)");

      ctx.fillStyle = heatGradient;
      ctx.fillRect(
        x - heat.radius,
        y - heat.radius,
        heat.radius * 2,
        heat.radius * 2
      );

      return true;
    });

    ctx.restore();
  }

  clear(): void {
    this.heatPoints = [];
  }
}

/**
 * Floating text renderer class
 */
export class FloatingTextRenderer {
  private texts: FloatingText[] = [];

  addText(x: number, y: number, text: string, color: string): void {
    this.texts.push({
      x,
      y,
      text,
      opacity: 1,
      offsetY: 0,
      color,
      ageMs: 0,
    });
  }

  updateAndDraw(
    ctx: CanvasRenderingContext2D,
    deltaMs: number
  ): Array<{ x: number; y: number; color: string }> {
    const particleRequests: Array<{ x: number; y: number; color: string }> = [];

    this.texts = this.texts.filter((text) => {
      text.ageMs += deltaMs;

      const maxAgeMs = ANIMATION_CONFIG.FLOATING_TEXT_MAX_AGE_MS;
      const t = text.ageMs / maxAgeMs;

      // Movement and opacity
      text.offsetY =
        -Math.pow(t, 2) *
        ctx.canvas.height *
        ANIMATION_CONFIG.FLOATING_TEXT_RISE_MULTIPLIER;
      text.opacity = t < 0.2 ? 1 : 1 - (t - 0.2) / 0.8;

      // Spawn particles when text is fading out
      if (text.opacity < 0.3 && !text.particlesSpawned) {
        particleRequests.push({
          x: text.x,
          y: text.y + text.offsetY,
          color: text.color,
        });
        text.particlesSpawned = true;
      }

      if (text.ageMs >= maxAgeMs) return false;

      // Draw text
      ctx.save();
      ctx.globalAlpha = text.opacity;
      ctx.fillStyle = text.color;

      const isUserBet = text.color.includes("ff");
      ctx.font = isUserBet ? "bold 24px monospace" : "bold 18px monospace";
      ctx.textAlign = "center";

      ctx.fillText(text.text, text.x, text.y + text.offsetY);
      ctx.restore();

      return true;
    });

    return particleRequests;
  }

  clear(): void {
    this.texts = [];
  }
}
